import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { EdgeUpdate, EdgeUpdateStatus } from '../api/types.js';
import { useApi } from '../auth.js';
import { Age, ErrorBox, Spinner } from '../components.js';
import { useNow, useResource } from '../hooks.js';
import { PLACEHOLDER } from '../lib/format.js';

/** While the newest update has no result from the edge yet. */
export const UPDATE_POLL_MS = 5000;

const STATUS_LABEL: Record<EdgeUpdateStatus, string> = {
  sent: 'Updating…',
  updated: 'Updated',
  rolled_back: 'Rolled back',
  failed: 'Failed',
};
const STATUS_CLASS: Record<EdgeUpdateStatus, string> = {
  sent: 'badge-config-sent',
  updated: 'badge-config-applied',
  rolled_back: 'badge-config-rejected',
  failed: 'badge-config-rejected',
};

function UpdateRow({ u, now }: { u: EdgeUpdate; now: number }) {
  return (
    <tr>
      <td className="mono">{u.tag}</td>
      <td>
        <span className={`badge ${STATUS_CLASS[u.status]}`}>{STATUS_LABEL[u.status]}</span>
        {u.error && <div className="bad small">{u.error}</div>}
      </td>
      <td className="mono">{u.previous_tag ?? PLACEHOLDER}</td>
      <td>
        <Age ts={u.created_at} now={now} />
      </td>
      <td>
        <Age ts={u.finished_at} now={now} />
      </td>
    </tr>
  );
}

/**
 * Agent version of one edge (§4 "Updates"): pick a version from the
 * registry and send it; the edge's updater switches and rolls back when the
 * new agent is not healthy within 5 minutes.
 */
export function EdgeUpdatePage() {
  const { edgeId = '' } = useParams();
  const api = useApi();
  const now = useNow(5000);
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const images = useResource('edge-images', (s) => api.edgeImages(s));
  const [polling, setPolling] = useState(false);
  const updates = useResource(
    `edge-updates:${edgeId}`,
    (s) => api.edgeUpdates(edgeId, s),
    polling ? UPDATE_POLL_MS : undefined,
  );
  const data = updates.data;
  const current = data?.items[0];
  const inProgress = current?.status === 'sent';
  if (inProgress !== polling) setPolling(inProgress);

  async function send() {
    if (
      !tag ||
      !window.confirm(`Update the agent to ${tag}? It restarts; a failed update rolls back.`)
    ) {
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await api.startUpdate(edgeId, tag);
      updates.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="breadcrumbs">
        <Link to="/sites">Sites</Link> /
      </div>
      <h1>Agent version</h1>
      {updates.error && <ErrorBox error={updates.error} />}
      {!data ? (
        <Spinner />
      ) : (
        <>
          <p>
            Running <span className="mono strong">{data.agent_version ?? PLACEHOLDER}</span>
            {inProgress && (
              <span className="muted"> · updating to {current.tag}, up to 5 minutes…</span>
            )}
          </p>
          <div className="toolbar">
            {images.error && <ErrorBox error={images.error} />}
            <select
              aria-label="Version"
              value={tag}
              onChange={(e) => {
                setTag(e.target.value);
              }}
              disabled={!images.data || inProgress}
            >
              <option value="">Choose a version…</option>
              {images.data?.tags.map((t) => (
                <option key={t} value={t} disabled={t === data.agent_version}>
                  {t}
                  {t === data.agent_version ? ' (running)' : ''}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              disabled={!tag || busy || inProgress}
              onClick={() => void send()}
            >
              Update
            </button>
          </div>
          {actionError && <ErrorBox error={actionError} />}
          <h2>History</h2>
          {data.items.length === 0 ? (
            <p className="muted">No updates yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Result</th>
                  <th>Before</th>
                  <th>Sent</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u) => (
                  <UpdateRow key={u.id} u={u} now={now} />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
